/**
 * Export format policy: match upload format during design; STL-only on finalization
 * or when a locking attachment apply is the active (head) history command.
 */

import { MAIN_PROCESS_STEPS } from '../constants/flowConfig';
import { EXPORT_FORMATS } from './exportConfig';
import useEditorStore from '../stores/useEditorStore';
import useLockingSystemStore from '../stores/useLockingSystemStore';

const ALL_FORMATS = [EXPORT_FORMATS.STL, EXPORT_FORMATS.PLY, EXPORT_FORMATS.OBJ];

/**
 * @param {string|null|undefined} type - uploadedModelData.type
 * @returns {'stl'|'ply'|'obj'|null}
 */
export function normalizeUploadFormatType(type) {
  if (type === 'stl' || type === 'ply' || type === 'obj') return type;
  return null;
}

/**
 * Infer format from a static model path (e.g. debug default path).
 * @param {string|null|undefined} path
 * @returns {'stl'|'ply'|'obj'|null}
 */
export function inferFormatFromModelPath(path) {
  if (typeof path !== 'string') return null;
  const i = path.lastIndexOf('.');
  if (i < 0) return null;
  const ext = path.slice(i).toLowerCase();
  if (ext === '.stl') return 'stl';
  if (ext === '.ply') return 'ply';
  if (ext === '.obj') return 'obj';
  return null;
}

/**
 * Preferred export format from session (upload wins over default path).
 */
export function getSourceExportFormat(uploadedModelData, selectedModelPath) {
  const fromUpload = normalizeUploadFormatType(uploadedModelData?.type);
  if (fromUpload) return fromUpload;
  const fromPath = inferFormatFromModelPath(selectedModelPath);
  if (fromPath) return fromPath;
  return EXPORT_FORMATS.STL;
}

/**
 * True when export must be STL only (main finalization step, or locking attachment apply is current history head).
 * @param {string} currentStep - useWorkflowStore currentStep
 */
export function isExportForcedToStl(currentStep) {
  if (currentStep === MAIN_PROCESS_STEPS.FINALIZATION) return true;

  const { history, historyIndex } = useEditorStore.getState();
  const cmd = useLockingSystemStore.getState().currentAttachmentCommand;
  if (!cmd) return false;
  return historyIndex >= 0 && history[historyIndex] === cmd;
}

/**
 * Effective format for downloads (respects STL-only policy).
 */
export function getEffectiveExportFormat(currentStep, uploadedModelData, selectedModelPath) {
  if (isExportForcedToStl(currentStep)) return EXPORT_FORMATS.STL;
  return getSourceExportFormat(uploadedModelData, selectedModelPath);
}

/**
 * Formats to show in the export UI (STL-only policy => single option).
 */
export function getAllowedExportFormats(currentStep, uploadedModelData, selectedModelPath) {
  if (isExportForcedToStl(currentStep)) {
    return [EXPORT_FORMATS.STL];
  }
  const preferred = getSourceExportFormat(uploadedModelData, selectedModelPath);
  if (!ALL_FORMATS.includes(preferred)) return [...ALL_FORMATS];
  return [preferred, ...ALL_FORMATS.filter((f) => f !== preferred)];
}
